import { ChangeEvent, FC, useCallback, useRef, useState } from "react"
import { OdResourcePageProps } from "@/types/resource"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { Card, CardHeader, Divider, Box, IconButton, TextField as MuiTextField, Tooltip, Typography, Button } from "@mui/material"
import { ListContextProvider, useDeleteWithConfirmController, useListContext, useRefresh } from "ra-core"
import { Datagrid } from "@/components/list/datagrid"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { FunctionField, TextField } from "@/components/field"
import useLocalListController from "@/components/list/useLocalListController"
import { DateField2 } from "@/components/field/DateField2"
import { EncryptionKeyResponseDto } from "@/gen/api/encrypt"
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import WarningIcon from '@mui/icons-material/Warning'
import AddIcon from '@mui/icons-material/Add'
import Confirm from "@/components/widgets/Confirm"
import InfoIcon from '@mui/icons-material/Info';
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers"
import CreateOrEdit from "@/controller/CreateOrEdit"
import EditEncryptionKey from "@/components/settings/encryption/EditEncryptionKey"


const EncryptionKeyList: FC<OdResourcePageProps> = (props) => {
  const { resource } = props
  const listCtrl = useLocalListController({ resource, basePath: '', filter: { } })
  return (
    <ListContextProvider value={listCtrl}>
      <EncryptionKeyListBody {...props} />
    </ListContextProvider>
  )
}


const EncryptionKeyListBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const listCtx = useListContext<EncryptionKeyResponseDto & RecPatch>()
  
  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])

  const [openEdit, setOpenEdit] = useState(false)
  const [recordToEdit, setRecordToEdit] = useState<EncryptionKeyResponseDto | null>(null)
  const savedHandlers = useDefaultSavedHandlers(() => `${recordToEdit ? 'Edit' : 'Create'} Encryption Key`)

  const recordToDel = useRef<EncryptionKeyResponseDto&RecPatch | null>(null)
  const [nameConfirm, setNameConfirm] = useState('')

  const nameConfirmed = (recordToDel.current?.name?.toLowerCase() === nameConfirm?.toLowerCase())

  const handleNameInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setNameConfirm(e.target.value)
  }, [])
  const delHandlers = useDefaultSavedHandlers('Delete Encryption Key')
  delHandlers.onSuccess.merge(refreshLight)

  const {
    open: openConfirm,
    loading: deleting,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    record: recordToDel.current as any,
    resource,
    ...delHandlers,
  })

  return (
    <Card>
      <CardHeader 
        title={
          <>
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              Encryption Keys
              <Tooltip title="Encryption Keys are used to encrypt data fields in databases or structured data files.">
                <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
              </Tooltip>
            </Box>
          </>
        }
        action={
          <Button 
            variant="contained" 
            color="primary" 
            size="small" 
            startIcon={<AddIcon />}
            onClick={() => { setOpenEdit(true); setRecordToEdit(null) }}
          >
            New Key
          </Button>
        }
      />
      <Divider />
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>
          <Datagrid 
            hasBulkActions={false} 
            sx={{ 
              '& th' : { py: 2 },
              '& td' : { py: 2 },
              '& .key-name': { maxWidth: 750, display: 'inline-block', wordBreak: 'break-word' },
            }}
            {...listCtx}
          >
            <DateField2 source="createdTime" label="Created Time" sortable={false} showTime={true} />
            <TextField source="name" label="Name" className="key-name" sortable={false} />
            <FunctionField 
              source="name" 
              label="Action" 
              sortable={false} 
              render={(d?:EncryptionKeyResponseDto&RecPatch) => (
                <Tooltip title="Delete">
                  <IconButton
                    sx={{
                      color: 'text.primary' as any,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={(e:any) => {
                      recordToDel.current = d as any
                      setNameConfirm('')
                      handleDialogOpen(e)
                    }}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            />
          </Datagrid>
        </Box>
      </Scrollbar>
      {//@ts-ignore 
      <DefaultPagination {...listCtx} />}
      
      <CreateOrEdit resource={resource} basePath="" record={recordToEdit} onFailure={savedHandlers.onFailure} onSuccess={savedHandlers.onSuccess.merge(refreshLight)}>
        {openEdit &&
          <EditEncryptionKey open={openEdit} setOpen={setOpenEdit} />
        }
      </CreateOrEdit>

      <Confirm
        isOpen={openConfirm}
        loading={deleting}
        valid={nameConfirmed}
        title={"Delete Encryption Key"}
        onConfirm={handleDelete}
        onClose={(e:any) => {handleDialogClose(e); recordToDel.current = null; setNameConfirm('')}}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon fontSize="large" sx={{ color: 'warning.main' }}/>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Warning! Deleting Encryption Key "{recordToDel.current?.name}" is irreversible. The action you are about to take cannot be undone.
              </Typography>
            </Box>
            <MuiTextField
              id="confirm-connection-name"
              autoFocus
              fullWidth
              error={!nameConfirmed}
              helperText={!nameConfirmed && "Enter the name of your Encryption Key to confirm delete."}
              size="medium"
              label="Type Encryption Key Name"
              sx={{ mb: 2, mt: 4, }}
              onChange={handleNameInput}
              value={nameConfirm}
            />
          </Box>
        )}
      />
    </Card>
  )
}

export default EncryptionKeyList;
